import { useLocation } from "react-router-dom";
import eridonLogo from "@/assets/eridon-logo.png";
import { motion } from "framer-motion";
import {
  LayoutDashboard,
  BrainCircuit,
  FileText,
  Key,
  BarChart3,
  Settings,
  Inbox,
  Wand2,
  MessageCircle,
  Calculator,
  Megaphone,
} from "lucide-react";
import { NavLink } from "@/components/NavLink";
import { AliveIcon, type AliveAnimation } from "@/components/ui/AliveIcon";
import { useAuth } from "@/contexts/AuthContext";
import {
  Sidebar,
  SidebarContent,
  SidebarGroup,
  SidebarGroupContent,
  SidebarGroupLabel,
  SidebarMenu,
  SidebarMenuButton,
  SidebarMenuItem,
  SidebarHeader,
  useSidebar,
} from "@/components/ui/sidebar";

type NavItem = { title: string; url: string; icon: typeof LayoutDashboard; animation: AliveAnimation };

const dailyItems: NavItem[] = [
  { title: "Дашборд", url: "/", icon: LayoutDashboard, animation: "pulse" },
  { title: "Нейро-Инбокс", url: "/inbox", icon: BrainCircuit, animation: "glow" },
  { title: "Контракты", url: "/contracts", icon: FileText, animation: "tilt" },
  { title: "Токены (erid)", url: "/tokens", icon: Key, animation: "spin" },
  { title: "ИИ-маркировка", url: "/mark", icon: Wand2, animation: "glow" },
  { title: "Само-промо", url: "/self-promo", icon: Megaphone, animation: "pulse" },
];

const managementItems: NavItem[] = [
  { title: "Отчёты", url: "/reports", icon: BarChart3, animation: "bounce" },
  { title: "Налог 3% (ЕРИР)", url: "/tax-monitoring", icon: Calculator, animation: "pulse" },
  { title: "API-ключи", url: "/api-keys", icon: Key, animation: "glow" },
  { title: "Настройки", url: "/settings", icon: Settings, animation: "spin" },
];

export function AppSidebar() {
  const { state } = useSidebar();
  const collapsed = state === "collapsed";
  const location = useLocation();
  const { hasRole } = useAuth();
  const isAdmin = hasRole("admin");

  const adminItems: NavItem[] = isAdmin
    ? [
        { title: "Сообщения", url: "/admin/messages", icon: Inbox, animation: "pulse" },
        { title: "Обратная связь", url: "/admin/feedback", icon: MessageCircle, animation: "glow" },
      ]
    : [];

  const renderGroup = (label: string, items: NavItem[]) => (
    <SidebarGroup>
      <SidebarGroupLabel className="text-[10px] uppercase tracking-widest text-muted-foreground/60">
        {label}
      </SidebarGroupLabel>
      <SidebarGroupContent>
        <SidebarMenu>
          {items.map((item) => (
            <SidebarMenuItem key={item.title}>
              <SidebarMenuButton
                asChild
                isActive={location.pathname === item.url}
                tooltip={item.title}
              >
                <NavLink
                  to={item.url}
                  end={item.url === "/"}
                  className="transition-colors"
                  activeClassName="bg-sidebar-accent text-sidebar-accent-foreground"
                >
                  <AliveIcon icon={item.icon} animation={item.animation} className="h-4 w-4" />
                  <span>{item.title}</span>
                </NavLink>
              </SidebarMenuButton>
            </SidebarMenuItem>
          ))}
        </SidebarMenu>
      </SidebarGroupContent>
    </SidebarGroup>
  );

  return (
    <Sidebar collapsible="icon" className="border-r border-sidebar-border">
      <SidebarHeader className="p-4">
        <div className="flex items-center gap-3">
          <img src={eridonLogo} alt="ERIDON логотип" className="h-9 w-9 shrink-0" width={36} height={36} />
          {!collapsed && (
            <motion.div
              initial={{ opacity: 0, x: -10 }}
              animate={{ opacity: 1, x: 0 }}
              className="flex flex-col"
            >
              <span className="text-sm font-bold tracking-wide text-foreground">
                ERIDON
              </span>
              <span className="text-[10px] text-muted-foreground">
                ФЗ-347 / ФЗ-168
              </span>
            </motion.div>
          )}
        </div>
      </SidebarHeader>

      <SidebarContent>
        {renderGroup("Ежедневно", dailyItems)}
        {renderGroup("Управление", managementItems)}
        {adminItems.length > 0 && renderGroup("Администрирование", adminItems)}
      </SidebarContent>
    </Sidebar>
  );
}
